import type { PlanRequest, PlanResponse, PlannerConfig } from '../shared/contracts.js';
import type { TripStatus } from '../shared/trip-api.js';
import { planKey, subtripPlanRequest, type Trip } from '../shared/trips.js';
import { PlanCoordinator } from './planning.js';
import { TripApiError, type TripApi } from './trip-api.js';
import { TripSaver, type SaveState } from './trip-saver.js';
import { track, errorDetail } from './telemetry.js';
import { observe } from './telemetry.js';
import { operation, type ObservedOperation } from '../shared/observability.js';
import { errorOutcome } from './observed-request.js';

export interface SubtripPlan { key: string; plan: PlanResponse }
export type SubtripPlans = Record<string, SubtripPlan>;

/** Switching trips does not assume the old editor's outstanding engine work stopped. */
class PlanLane {
  private running?: Promise<void>;
  async run<T>(work: () => Promise<T>): Promise<T> {
    while (this.running) await this.running;
    let release!: () => void;
    this.running = new Promise((resolve) => { release = resolve; });
    try { return await work(); }
    finally { this.running = undefined; release(); }
  }
}
const pagePlans = new PlanLane();
const pageMemory = new WeakMap<TripApi, Map<string, { cache: Map<string, PlanResponse>; tried: Set<string> }>>();

export interface EditorState {
  trip: Trip;
  status: TripStatus;
  plans: SubtripPlans;
  index: number;
  save: SaveState;
  busy: boolean;
  loading: boolean;
  deleting: boolean;
  error?: string;
  notice?: string;
}

/** Busy is an answered refusal, not uncertain engine work: only this error is retried. */
export async function planStoredWhenFree(api: TripApi, id: string, subtripId: string, revision: number,
  active: () => boolean = () => true, wait: () => Promise<void> = () => new Promise((resolve) => setTimeout(resolve, 1000)), observation?: ObservedOperation) {
  for (let attempt = 0; ; attempt++) {
    if (!active()) throw new Error('This plan was superseded.');
    try { return await api.plan(id, { subtripId, revision }, observation); }
    catch (error) {
      if (!(error instanceof TripApiError && error.code === 'PLANNER_BUSY') || attempt >= 30) throw error;
      await wait();
    }
  }
}

/** The open trip's one save writer and one newest-wins planning coordinator. */
export class TripEditor {
  state: EditorState;
  private listeners = new Set<() => void>();
  private cache = new Map<string, PlanResponse>();
  private tried = new Set<string>();
  private jobs = new WeakMap<PlanRequest, { id: string; key: string; edit: number; generation: number }>();
  private saver: TripSaver;
  private coordinator: PlanCoordinator;
  private disposed = false;
  private generation = 0;
  private refreshTask?: Promise<void>;
  private refreshAgain = false;
  private refreshConflict = false;
  private refreshSaveConflict = false;
  private planLoad = 0;
  private scheduled?: string;
  private paused = false;
  private unreadablePlans = new Set<string>();
  private readingPlans = new Map<string, symbol>();

  constructor(trip: Trip, status: TripStatus, private readonly limits: PlannerConfig, private readonly api: TripApi,
    private readonly onSaved: () => void, private readonly onDeleted: () => void, private readonly settleMs = 350) {
    let memories = pageMemory.get(api);
    if (!memories) { memories = new Map(); pageMemory.set(api, memories); }
    let memory = memories.get(trip.id);
    if (!memory) { memory = { cache: new Map(), tried: new Set() }; memories.set(trip.id, memory); }
    this.cache = memory.cache;
    this.tried = memory.tried;
    this.state = { trip, status, plans: {}, index: 0, save: { status: status.revision ? 'saved' : 'draft' }, busy: false, loading: true, deleting: false };
    this.saver = this.createSaver(status.revision);
    this.coordinator = new PlanCoordinator((request) => pagePlans.run(async () => {
      const job = this.jobs.get(request)!;
      const revision = await this.saver.waitFor(job.edit);
      if (this.disposed || this.paused || job.generation !== this.generation || this.keyFor(job.id) !== job.key) throw new Error('This plan was superseded.');
      this.tried.add(`${job.id}:${job.key}`);
      const started = performance.now();
      const observation = operation(observe, 'plan', { tripId: trip.id, subtripId: job.id });
      try {
        const reply = await planStoredWhenFree(api, trip.id, job.id, revision, () => !this.disposed && !this.paused && job.generation === this.generation && this.keyFor(job.id) === job.key, undefined, observation);
        observation.finish(this.disposed || this.paused || job.generation !== this.generation || this.keyFor(job.id) !== job.key ? 'superseded' : 'success', { planId: reply.plan.planId, stored: reply.stored });
        track('plan-result', { tripId: trip.id, planId: reply.plan.planId, detail: { ms: Math.round(performance.now() - started), stored: reply.stored } });
        // Even an unstored result is usable only while this exact content is on screen.
        if (!this.disposed && !this.paused && this.keyFor(job.id) === job.key) {
          this.invalidatePlanLoads();
          this.cache.set(`${job.id}:${job.key}`, reply.plan);
          this.change({ plans: { ...this.state.plans, [job.id]: { key: job.key, plan: reply.plan } } });
        }
        if (!this.disposed && reply.stored) { void this.refresh(); onSaved(); }
        return reply.plan;
      } catch (error) {
        observation.finish(this.disposed || this.paused || job.generation !== this.generation || this.keyFor(job.id) !== job.key ? 'superseded' : errorOutcome(error));
        track('plan-error', { tripId: trip.id, ...errorDetail(error) });
        if (error instanceof TripApiError && error.code === 'TRIP_CONFLICT') {
          // A stale revision never tried this content on the engine.
          this.tried.delete(`${job.id}:${job.key}`);
          void this.refresh(true);
        }
        throw error;
      }
    }), {
      onResult: () => {},
      onError: (error) => {
        // The conflict reload owns its notice/error, not the refused plan.
        if (!(error instanceof TripApiError && error.code === 'TRIP_CONFLICT')) this.change({ error: error.message });
      },
      onBusy: (busy) => {
        this.change({ busy });
        if (!busy) { this.scheduled = undefined; this.resume(); }
      }
    });
  }

  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  snapshot = () => this.state;

  private change(update: Partial<EditorState>): void {
    if (this.disposed) return;
    this.state = { ...this.state, ...update };
    for (const listener of this.listeners) listener();
  }

  private createSaver(revision: number): TripSaver {
    return new TripSaver(revision, (revision, trip, observation) => this.api.put(trip.id, { revision, trip }, observation), {
      state: (save) => this.change({ save }),
      saved: (status) => {
        this.change({ status });
        queueMicrotask(() => { if (!this.disposed) { this.onSaved(); this.resume(); } });
      },
      conflict: () => { void this.queueRefresh(true, true); }
    });
  }

  async start(): Promise<void> {
    await this.loadPlans();
  }

  update(trip: Trip): void {
    if (this.state.deleting) return;
    this.generation++;
    this.coordinator.invalidate();
    this.scheduled = undefined;
    this.paused = false;
    this.change({ trip, index: Math.min(this.state.index, trip.subtrips.length - 1), error: undefined });
    this.saver.save(trip);
    this.pump();
  }

  select(index: number): void {
    this.generation++;
    this.coordinator.invalidate();
    this.scheduled = undefined;
    this.paused = false;
    this.change({ index, error: undefined });
    this.resume();
  }

  pause(): void { this.generation++; this.paused = true; this.scheduled = undefined; this.coordinator.invalidate(); }
  retry(): void {
    const sub = this.state.trip.subtrips[this.state.index]!;
    if (this.refreshConflict || this.unreadablePlans.has(sub.id)) { void this.refresh(); return; }
    this.tried.delete(`${sub.id}:${this.keyFor(sub.id)}`);
    this.paused = false;
    this.change({ error: undefined });
    this.pump();
  }
  dismissNotice(): void { this.change({ notice: undefined }); }
  async saved(): Promise<number> {
    for (;;) {
      const writer = this.saver;
      const edit = writer.latestEdit;
      const revision = await writer.waitFor(edit);
      if (writer === this.saver && edit === writer.latestEdit) return revision;
    }
  }
  async prepareDelete(): Promise<void> {
    this.pause();
    this.change({ deleting: true });
    try { await this.saved(); }
    catch (error) { this.cancelDelete(); throw error; }
  }
  cancelDelete(): void { this.change({ deleting: false }); this.paused = false; this.resume(); }

  private resume(): void {
    if (this.refreshAgain) void this.refresh();
    else this.pump();
  }

  private keyFor(id: string): string | undefined {
    const index = this.state.trip.subtrips.findIndex((sub) => sub.id === id);
    if (index < 0) return undefined;
    const result = subtripPlanRequest(this.state.trip, index, this.limits.maxRoutePoints, this.limits.maxExtraMinutes);
    return 'request' in result ? planKey(result.request) : undefined;
  }

  private pump(): void {
    if (this.disposed || this.paused || this.refreshTask || this.refreshAgain || this.refreshConflict || this.state.loading || this.state.busy || this.scheduled || this.state.status.planning) return;
    if (this.state.save.status === 'unsaved' && !this.state.save.retryMs) return;
    const { trip, index, plans } = this.state;
    const order = [index, ...trip.subtrips.map((_sub, i) => i).filter((i) => i !== index)];
    for (const i of order) {
      const sub = trip.subtrips[i]!;
      if (this.readingPlans.has(sub.id)) continue;
      if (this.unreadablePlans.has(sub.id) && this.state.status.plans[sub.id]?.current) continue;
      const result = subtripPlanRequest(trip, i, this.limits.maxRoutePoints, this.limits.maxExtraMinutes);
      if (!('request' in result)) continue;
      const key = planKey(result.request);
      if (plans[sub.id]?.key === key) continue;
      const identity = `${sub.id}:${key}`;
      const cached = this.cache.get(identity);
      if (cached) { this.change({ plans: { ...this.state.plans, [sub.id]: { key, plan: cached } } }); continue; }
      if (this.tried.has(identity)) continue;
      this.scheduled = identity;
      this.jobs.set(result.request, { id: sub.id, key, edit: this.saver.latestEdit, generation: this.generation });
      this.coordinator.schedule(result.request, this.settleMs);
      return;
    }
  }

  private async loadPlans(): Promise<void> {
    const load = ++this.planLoad;
    const { trip, status } = this.state;
    this.readingPlans.clear();
    this.change({ loading: true, error: undefined });
    try {
      await Promise.all(trip.subtrips.map(async (sub) => {
        const stored = status.plans[sub.id];
        if (!stored?.current) return;
        const key = this.keyFor(sub.id);
        if (!key) return;
        const shown = this.state.plans[sub.id];
        if (shown?.key === key && shown.plan.planId === stored.planId) {
          this.unreadablePlans.delete(sub.id);
          return;
        }
        const current = () => !this.disposed && load === this.planLoad && this.keyFor(sub.id) === key;
        const reading = Symbol();
        this.readingPlans.set(sub.id, reading);
        try {
          const plan = await this.api.storedPlan(trip.id, sub.id);
          if (!current()) return;
          this.unreadablePlans.delete(sub.id);
          this.cache.set(`${sub.id}:${key}`, plan);
          this.change({ plans: { ...this.state.plans, [sub.id]: { key, plan } } });
        } catch (error) {
          if (!current()) return;
          if (!(error instanceof TripApiError && error.code === 'PLAN_NOT_CURRENT')) {
            this.unreadablePlans.add(sub.id);
            this.change({ error: error instanceof Error ? error.message : String(error) });
            return;
          }
          this.unreadablePlans.delete(sub.id);
          // A graph change can invalidate an apparently current record.
          this.cache.delete(`${sub.id}:${key}`);
          const plans = { ...this.state.plans };
          delete plans[sub.id];
          this.change({ plans });
        } finally {
          if (this.readingPlans.get(sub.id) === reading) this.readingPlans.delete(sub.id);
        }
      }));
    } finally {
      if (!this.disposed && load === this.planLoad) {
        this.change({ loading: false });
        this.resume();
      }
    }
  }

  private invalidatePlanLoads(): void {
    this.planLoad++;
    this.readingPlans.clear();
    this.change({ loading: false });
  }

  refresh(conflict = false): Promise<void> {
    return this.queueRefresh(conflict);
  }

  private queueRefresh(conflict: boolean, saveConflict = false): Promise<void> {
    if (this.disposed || this.state.status.revision === 0 && !conflict) return Promise.resolve();
    this.refreshAgain = true;
    this.refreshConflict ||= conflict;
    this.refreshSaveConflict ||= saveConflict;
    if (this.refreshTask) return this.refreshTask;
    if (!this.canRefresh()) return Promise.resolve();
    this.refreshTask = this.drainRefreshes().finally(() => {
      this.refreshTask = undefined;
      this.resume();
    });
    return this.refreshTask;
  }

  private canRefresh(): boolean {
    // Only a refused PUT can discard unsent edits. A refused plan waits for the writer.
    return !this.disposed && (this.refreshSaveConflict || !this.saver.hasEdits && (this.refreshConflict || !this.paused));
  }

  private async drainRefreshes(): Promise<void> {
    while (this.refreshAgain && this.canRefresh()) {
      const conflict = this.refreshConflict;
      const saveConflict = this.refreshSaveConflict;
      this.refreshAgain = false;
      this.refreshConflict = false;
      this.refreshSaveConflict = false;
      const generation = this.generation;
      const planLoad = this.planLoad;
      const superseded = () => generation !== this.generation || planLoad !== this.planLoad;
      try {
        const reply = await this.api.get(this.state.trip.id);
        if (this.disposed) return;
        if (superseded() || this.saver.hasEdits && !saveConflict) {
          this.refreshAgain = true;
          this.refreshConflict ||= conflict;
          this.refreshSaveConflict ||= saveConflict;
          continue;
        }
        const changed = reply.revision !== this.state.status.revision;
        if (changed || conflict) {
          this.generation++;
          this.coordinator.invalidate();
          this.scheduled = undefined;
          this.saver.dispose();
          this.saver = this.createSaver(reply.revision);
          const ids = new Set(reply.trip.subtrips.map((sub) => sub.id));
          const plans = Object.fromEntries(Object.entries(this.state.plans).filter(([id]) => ids.has(id)));
          this.change({ trip: reply.trip, status: reply, plans, save: { status: 'saved' }, index: Math.min(this.state.index, reply.trip.subtrips.length - 1), notice: 'This trip changed on another device. The latest version is shown.', error: undefined });
        } else this.change({ status: reply, error: undefined });
        await this.loadPlans();
      } catch (error) {
        if (this.disposed) return;
        if (superseded()) {
          this.refreshAgain = true;
          this.refreshConflict ||= conflict;
          this.refreshSaveConflict ||= saveConflict;
          continue;
        }
        if (error instanceof TripApiError && error.code === 'TRIP_NOT_FOUND') {
          this.refreshAgain = false;
          this.refreshConflict = false;
          this.refreshSaveConflict = false;
          this.onDeleted();
          return;
        }
        this.change({ error: error instanceof Error ? error.message : String(error) });
        // A failed conflict reload still needs to replace the writer on the next event.
        this.refreshConflict ||= conflict;
        this.refreshSaveConflict ||= saveConflict;
      }
    }
  }

  dispose(): void {
    this.disposed = true;
    this.generation++;
    this.planLoad++;
    this.refreshAgain = false;
    this.refreshConflict = false;
    this.refreshSaveConflict = false;
    this.readingPlans.clear();
    this.saver.dispose();
    this.coordinator.dispose();
    this.listeners.clear();
  }
}
